/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { useMemo } from 'react';
import { utils, unstable } from '@broadleaf/admin-components';
import { get, merge } from 'lodash';

import {
  startPayPal,
  successPayPal,
  errorPayPal
} from 'app/paypal/components/PayPalView/state/actions';

const { RequestUtils } = utils;
const { MetadataUtils } = unstable;
const { getEndpointByType } = MetadataUtils;

const { request } = RequestUtils;

export default function usePayPalApi(context, dispatch, metadata) {
  return useMemo(
    () => ({
      accounts: {
        create: create(context, dispatch, metadata),
        finish: finish(context, dispatch, metadata),
        provision: provision(context, dispatch, metadata),
        retrieve: retrieve(context, dispatch, metadata),
        update: update(context, dispatch, metadata),
        unlink: unlink(context, dispatch, metadata)
      }
    }),
    [context, dispatch, metadata]
  );
}

function retrieve(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'READ_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function provision(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'PROVISION_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function create(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'CREATE_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function finish(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'FINISH_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function update(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'UPDATE_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function unlink(context, dispatch, metadata) {
  const config = {
    ...getEndpointByType(metadata, 'UNLINK_ACCOUNT')
  };
  return getCallback(config, context, dispatch);
}

function getCallback(config, context, dispatch) {
  return async ({ rejectOnError = false, ...additionalConfig }) => {
    dispatch(startPayPal());
    try {
      const response = await request(
        merge({}, config, additionalConfig),
        merge({}, context, get(additionalConfig, 'contextParams'))
      );
      dispatch(successPayPal(response.data));
      return response.data;
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      dispatch(errorPayPal(err));
      return rejectOnError && Promise.reject(err);
    }
  };
}
